<template>
  <span>
    <v-btn
      depressed
      dark
      height="40"
      class="toolbar-btn"
      color="#6ec39b"
      :disabled="!rows.length"
      @click="dialog = true"
    >
      <v-icon left small>mdi-file-excel</v-icon>
      ดาวน์โหลด Excel ({{ rows.length }})
    </v-btn>

    <v-dialog v-model="dialog" max-width="480" :persistent="exporting">
      <v-card>
        <v-card-title>ดาวน์โหลด Excel</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="tw-pt-4">
          <v-simple-table dense>
            <tbody>
              <tr v-for="s in summaryRows" :key="s.label">
                <td class="tw-text-gray-600" style="width: 140px">{{ s.label }}</td>
                <td>{{ s.value }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <div class="tw-text-xs tw-text-gray-500 tw-mt-3">
            ยอดของใบลดหนี้ (CR) เป็นค่าติดลบ ผลรวมทั้งคอลัมน์จึงเป็นยอดสุทธิ
          </div>

          <div v-if="exporting && showProgress" class="tw-mt-4">
            <div class="tw-text-sm tw-mb-1">
              {{ progressText }}
            </div>
            <v-progress-linear :value="progress" color="#6ec39b" height="8" rounded></v-progress-linear>
          </div>
          <v-alert v-if="error" type="error" text dense class="tw-mt-4 tw-mb-0">
            สร้างไฟล์ไม่สำเร็จ — {{ error }}
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="exporting" @click="dialog = false">ยกเลิก</v-btn>
          <v-btn
            depressed
            dark
            color="#6ec39b"
            :loading="exporting"
            @click="exportExcel"
          >
            <v-icon left small>mdi-download</v-icon>
            ดาวน์โหลด
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
/**
 * Excel export for the approved Lao eTax documents page (documents.approved.vue).
 *
 * Confirm first: the dialog restates what the file will contain (row count,
 * filters, net total) so a user who forgot a filter is not surprised by the
 * file. The export itself runs in chunks and yields between them, so a large
 * register keeps the page responsive and shows a progress bar instead of
 * freezing the tab.
 *
 * Credit notes (CR) are NEGATIVE so a plain SUM of the amount column is the
 * net — the same convention as the Thai tax report (docs.js getListByMonth).
 *
 * The file carries the SAP writeback state too (constants/laoSapSync.js), so
 * the export doubles as the reconciliation list against SAP B1 — which is why
 * the confirm dialog restates the SAP filter along with the others: a file
 * narrowed to "ซิงค์ไม่สำเร็จ" must not be mistaken for the month's register.
 */
import * as XLSX from "xlsx";

// The amount column is located by this exact string — see amountCol below.
const AMOUNT_HEADER = "ยอดรวม (ใบลดหนี้ติดลบ)";

const CHUNK = 500;
// Below this the file is built in well under a second; a progress bar would
// only flicker.
const PROGRESS_MIN_ROWS = 1000;

const nextTick = () => new Promise((resolve) => setTimeout(resolve, 0));

export default {
  name: "ApprovedExportDialog",
  props: {
    // Rows exactly as the page filters them (every match, not one table page).
    rows: { type: Array, required: true },
    // [{ label, prop }] — the company's optional columns, prop = row key.
    extraColumns: { type: Array, default: () => [] },
    // [{ label, value }] — filters in effect, shown in the confirm dialog.
    filters: { type: Array, default: () => [] },
    // "YYYY-MM" or "all"; goes into the file name.
    period: { type: String, default: "all" },
    typeLabel: { type: Function, required: true },
    sapLabel: { type: Function, required: true },
    formatIssueTime: { type: Function, required: true },
  },
  data() {
    return {
      dialog: false,
      exporting: false,
      progress: 0,
      error: "",
    };
  },
  computed: {
    progressText() {
      const step = this.progress >= 100 ? "กำลังบันทึกไฟล์..." : "กำลังสร้างไฟล์...";
      return step + " " + this.progress + "%";
    },
    showProgress() {
      return this.rows.length >= PROGRESS_MIN_ROWS;
    },
    // Only while the dialog is open. `rows` is the page's live filtered set,
    // so without this guard every snapshot — and the page's listener fires on
    // every document the verification poller approves and every SAP field the
    // connector writes back — re-reduced the whole month to redraw a summary
    // table nobody was looking at.
    netTotal() {
      if (!this.dialog) return 0;
      return this.rows.reduce((sum, r) => sum + this.signedAmount(r), 0);
    },
    summaryRows() {
      return [{ label: "จำนวนเอกสาร", value: this.rows.length.toLocaleString() + " รายการ" }]
        .concat(this.filters)
        .concat([
          {
            label: "ยอดรวมสุทธิ",
            value: this.netTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          },
        ]);
    },
  },
  watch: {
    dialog(open) {
      if (open) {
        this.error = "";
        this.progress = 0;
      }
    },
  },
  methods: {
    signedAmount(r) {
      const amount = (Number(r.grandTotal) || 0) / 100;
      return r.docType === "CR" ? -amount : amount;
    },
    async exportExcel() {
      if (this.exporting) return;
      this.exporting = true;
      this.error = "";
      this.progress = 0;
      try {
        // Freeze the set at click time: the page is live and a newly approved
        // document must not slip in halfway through the file.
        const rows = this.rows
          .slice()
          .sort((a, b) => (a.issueTime < b.issueTime ? 1 : a.issueTime > b.issueTime ? -1 : 0));
        const extra = this.extraColumns;
        const header = ["วันที่เอกสาร", "เลขที่เอกสาร", "ประเภท", "ชื่อผู้ซื้อ", "เลขผู้เสียภาษี"]
          .concat(extra.map((c) => c.label))
          .concat([
            AMOUNT_HEADER,
            "เลขที่ Lao eTax",
            // The sync state as a word, and the SAP journal entry beside it —
            // the handle someone reconciling the file against B1 looks up.
            // Blank for a row that never reached SAP, not a dash: the column
            // is meant to be filtered and counted in Excel.
            "การซิงค์ SAP",
            "SAP TransId",
            "เวลานำส่ง",
            "ลิงก์ตรวจสอบ",
          ]);
        // Found, not counted. This index drives the number format and the
        // total row, and it used to be a literal `4 + extra.length` — one more
        // column before the amount and every figure in the file would have
        // been formatted against the wrong column, silently.
        const amountCol = header.indexOf(AMOUNT_HEADER);

        const ws = XLSX.utils.aoa_to_sheet([header]);
        let net = 0;
        for (let i = 0; i < rows.length; i += CHUNK) {
          const chunk = rows.slice(i, i + CHUNK).map((r) => {
            const signed = this.signedAmount(r);
            net += signed;
            return [r.issueDate, r.id, this.typeLabel(r.docType), r.buyerName, r.buyerTaxID]
              .concat(extra.map((c) => r[c.prop] || ""))
              .concat([
                signed,
                r.invoiceNumber,
                this.sapLabel(r.sapState),
                r.sapTransId || "",
                this.formatIssueTime(r.issueTime),
                r.url,
              ]);
          });
          XLSX.utils.sheet_add_aoa(ws, chunk, { origin: -1 });
          // Building rows is the bulk of the work; the last 5% is the save.
          this.progress = Math.round(((i + chunk.length) / rows.length) * 95);
          await nextTick();
        }

        const totalRow = header.map(() => "");
        totalRow[amountCol - 1] = "รวมสุทธิ";
        totalRow[amountCol] = Math.round(net * 100) / 100;
        XLSX.utils.sheet_add_aoa(ws, [totalRow], { origin: -1 });

        for (let r = 1; r <= rows.length + 1; r++) {
          const cell = ws[XLSX.utils.encode_cell({ r, c: amountCol })];
          if (cell && typeof cell.v === "number") cell.z = "#,##0.00";
        }
        ws["!cols"] = header.map((h, i) => ({ wch: i === amountCol ? 22 : Math.max(12, h.length + 4) }));

        this.progress = 100;
        await nextTick();
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "ใบกำกับภาษีที่อนุมัติแล้ว");
        XLSX.writeFile(wb, `approved-tax-invoices_${this.period}.xlsx`);
        this.dialog = false;
      } catch (e) {
        this.error = (e && e.message) || String(e);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.toolbar-btn {
  border-radius: 10px;
}
</style>
